// frontend/src/pages/ArticlesList.js
import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import API_URL from '../config';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AiFillLike, AiOutlineLike } from "react-icons/ai";
import { MdFavorite, MdFavoriteBorder } from "react-icons/md";
import { IoStatsChart } from "react-icons/io5";
import { useLoading } from '../contexts/LoadingContext';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';

function ArticlesList() {
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [user, setUser] = useState(null); 

  useEffect(() => {
    const fetchUserAndArticles = async () => {
      showLoading();
      try {
        // 先獲取文章列表
        const articlesResponse = await axios.get(`${API_URL}/api/articles`);
        
        // 嘗試獲取當前使用者資訊，但不影響文章列表的顯示
        try {
          const userResponse = await axios.get(`${API_URL}/api/current_user`, { withCredentials: true });
          setUser(userResponse.data);
          
          // 如果有用戶資訊，更新文章的按讚和收藏狀態
          const articlesData = articlesResponse.data.map(article => ({
            ...article,
            isLiked: article.likes.some(userId => userId.toString() === userResponse.data._id.toString()),
            isFavorited: userResponse.data.favorites.includes(article._id)
          }));
          setArticles(articlesData);
        } catch (userError) {
          // 如果獲取用戶資訊失敗，只設置文章列表，不設置用戶狀態
          setArticles(articlesResponse.data);
          setUser(null);
        }
      } catch (error) {
        console.error('獲取文章列表時發生錯誤：', error);
        toast.error(t('fetchArticlesError'));
      } finally {
        hideLoading();
      }
    };
    
    fetchUserAndArticles();
  }, []);
  
  const handleUnauthorizedAction = () => {
    setShowLoginModal(true);
    setTimeout(() => {
      window.location.href = `${API_URL}/api/auth/google`;
    }, 3000);
  };

  const handleLike = async (id) => {
    if (!user) {
      handleUnauthorizedAction();
      return;
    }
  
    try {
      const response = await axios.post(`${API_URL}/api/articles/${id}/like`, {}, { withCredentials: true });
  
      setArticles(articles.map(article => 
        article._id === id 
          ? { 
              ...article, 
              isLiked: response.data.liked,
              likes: response.data.likeCount 
            } 
          : article
      ));
    } catch (err) {
      console.error('更新點讚數時發生錯誤：', err);
      toast.error(t('likeError'));
    }
  };
  
  const handleFavorite = async (id) => {
    if (!user) {
      handleUnauthorizedAction();
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/articles/${id}/favorite`, {}, { withCredentials: true });

      setArticles((prevArticles) =>
        prevArticles.map(article =>
          article._id === id ? { ...article, isFavorited: response.data.favorited } : article
        )
      );

      toast.success(response.data.favorited ? t('addedToFavorites') : t('removedFromFavorites'));
    } catch (err) {
      console.error('更新收藏狀態時發生錯誤：', err);
      toast.error(t('favoriteError'));
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title && article.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = filteredArticles.slice(startIdx, startIdx + itemsPerPage);
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white">
      <SEO
        title={t('articles')}
        description={t('articlesDescription')}
        image={`${API_URL}/images/articles-banner.jpg`}
        type="website"
        keywords="blockchain, articles, cryptocurrency, security, compliance"
      />

      <ToastContainer />

      {showLoginModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <h2 className="text-xl font-bold">{t('notLoggedIn')}</h2>
            <p className="mt-2">{t('redirectingToGoogleLogin')}</p>
            <div className="mt-4 flex justify-center">
              <img src="/loading.gif" alt="Loading..." className="w-16 h-16" />
            </div>
          </div>
        </div>
      )}

      {/* 新增：Hero Banner 與回區塊鏈學院按鈕 */}
      <div
        className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold"
        style={{ backgroundImage: `url('/images/myReportsBanner.png')` }}
      >
        {t('blockchainAcademy')}
      </div>

      {/* 面包屑導航：區塊鏈學院 > 文章列表 */}
      <div className="bg-gray-200 dark:bg-gray-700 py-2">
        <div className="container mx-auto px-4">
          <nav className="text-sm">
            <Link to="/academy" className="text-blue-500 hover:underline">
              {t('blockchainAcademy')}
            </Link>
            <span className="mx-2"> &gt; </span>
            <span>{t('articles')}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <h2 className="text-2xl font-bold mb-4">{t('articles')}</h2>

        <div className="mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('search')}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedArticles.map((article) => (
            <div key={article._id} className="p-4 border rounded bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-shadow duration-200">
              <h3 className="text-xl font-semibold">
                {i18n.language === "en" && article.translations && article.translations.en && typeof article.translations.en === "object"
                  ? article.translations.en.title || article.title
                  : article.title}
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                {new Date(article.createdAt).toLocaleString()}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <button onClick={() => handleLike(article._id)} className="text-red-500 flex items-center space-x-1">
                  {article.isLiked ? <AiFillLike /> : <AiOutlineLike />}
                  <span>{Array.isArray(article.likes) ? article.likes.length : article.likes || 0}</span>
                </button>

                <button onClick={() => handleFavorite(article._id)} className="text-yellow-500 flex items-center space-x-1">
                  {article.isFavorited ? <MdFavorite /> : <MdFavoriteBorder />}
                  <span>{article.isFavorited ? t('favorited') : t('favorite')}</span>
                </button>

                <span className="flex items-center space-x-1">
                  <IoStatsChart /> <span>{article.views != null ? article.views : 0}</span>
                </span>
              </div>
              <Link
                to={`/articles/${article._id}`}
                className="text-blue-500 hover:underline mt-2 inline-block"
              >
                {t('readMore')}
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-center space-x-2">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              className={`px-4 py-2 rounded ${currentPage === index + 1
                ? 'bg-blue-500 text-white'
                : 'bg-gray-300 dark:bg-gray-700 dark:text-white'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ArticlesList;
